import React, { useState, useEffect } from 'react';
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import api from '../services/api';
import { useToast } from './Toast';

const COLORS = ['#1D9E75', '#3B82F6', '#EF9F27', '#EC4899', '#8B5CF6'];

const CashFlow = ({ clientId }) => {
  const { addToast } = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [threshold, setThreshold] = useState(0);
  const [savingThreshold, setSavingThreshold] = useState(false);

  useEffect(() => {
    loadCashFlowData();
  }, [clientId]);

  const loadCashFlowData = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/dashboard/${clientId}/cash-flow`);
      if (response.success) {
        setData(response);
        setThreshold(response.alert_threshold);
      } else {
        addToast('❌ Erreur lors du chargement de la trésorerie', 'error');
      }
    } catch (error) {
      console.error('Erreur cash flow:', error);
      addToast('❌ Erreur de connexion', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveThreshold = async (e) => {
    e.preventDefault();
    setSavingThreshold(true);
    try {
      const response = await api.post(`/dashboard/${clientId}/cash-flow/threshold`, {
        alert_threshold: parseFloat(threshold)
      });
      if (response.success) {
        addToast('✅ Seuil d\'alerte enregistré !', 'success');
        loadCashFlowData(); // Recharger pour rafraîchir les alertes du forecast
      } else {
        addToast('❌ Erreur lors de l\'enregistrement', 'error');
      }
    } catch (error) {
      console.error('Erreur sauvegarde seuil:', error);
      addToast('❌ Erreur de connexion', 'error');
    } finally {
      setSavingThreshold(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ minHeight: '400px', background: '#F5F4ED' }}>
        <div style={{ textAlign: 'center' }}>
          <div className="animate-spin" style={{ width: '40px', height: '40px', border: '3px solid #1D9E75', borderTopColor: 'transparent', borderRadius: '50%', margin: '0 auto 12px' }}></div>
          <p style={{ color: '#73726C', fontSize: '14px' }}>Analyse des flux de trésorerie en cours...</p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  // Déterminer s'il y a une alerte dans les prévisions
  const hasForecastAlert = data.forecast.some(item => item.alerte);
  const alertMonth = data.forecast.find(item => item.alerte);

  // Formatter la devise
  const formatEuro = (val) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(val);
  };

  return (
    <div className="cashflow-container" style={{ fontFamily: 'Inter, system-ui, sans-serif', background: '#F5F4ED', padding: '20px', minHeight: '100vh', boxSizing: 'border-box' }}>
      
      {/* 1. CONFIGURATION DU SEUIL & CARTES SUMMARY */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px', marginBottom: '20px' }}>
        
        {/* Solde Actuel */}
        <div className="card" style={{ background: 'white', borderRadius: '12px', border: '1px solid #E8E7E3', padding: '20px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: '11px', color: '#73726C', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '600' }}>Solde bancaire actuel</div>
            <div style={{ fontSize: '28px', fontWeight: '700', color: data.current_balance >= 0 ? '#0F6E56' : '#DC2626', marginTop: '8px' }}>
              {formatEuro(data.current_balance)}
            </div>
          </div>
          <div style={{ fontSize: '11px', color: '#73726C', marginTop: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#1D9E75' }}></span>
            Mise à jour en temps réel (relevés importés)
          </div>
        </div>

        {/* Flux Moyens */}
        <div className="card" style={{ background: 'white', borderRadius: '12px', border: '1px solid #E8E7E3', padding: '20px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: '11px', color: '#73726C', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '600' }}>Moyenne des flux mensuels</div>
            <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                <span style={{ color: '#73726C' }}>🟢 Entrées :</span>
                <span style={{ fontWeight: '600', color: '#0F6E56' }}>+{formatEuro(data.average_monthly_inflow)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                <span style={{ color: '#73726C' }}>🔴 Sorties :</span>
                <span style={{ fontWeight: '600', color: '#2D2D2D' }}>-{formatEuro(data.average_monthly_outflow)}</span>
              </div>
            </div>
          </div>
          <div style={{ fontSize: '11px', borderTop: '1px solid #E8E7E3', paddingTop: '10px', marginTop: '12px', display: 'flex', justifyContent: 'space-between', color: '#73726C' }}>
            <span>Flux Net Moyen :</span>
            <span style={{ fontWeight: '600', color: (data.average_monthly_inflow - data.average_monthly_outflow) >= 0 ? '#0F6E56' : '#DC2626' }}>
              {(data.average_monthly_inflow - data.average_monthly_outflow) >= 0 ? '+' : ''}{formatEuro(data.average_monthly_inflow - data.average_monthly_outflow)}
            </span>
          </div>
        </div>

        {/* Seuil Alerte Configurable */}
        <div className="card" style={{ background: 'white', borderRadius: '12px', border: '1px solid #E8E7E3', padding: '20px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <form onSubmit={handleSaveThreshold}>
            <div style={{ fontSize: '11px', color: '#73726C', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '600' }}>Configuration de l'alerte</div>
            <div style={{ fontSize: '12px', color: '#73726C', marginTop: '4px' }}>Alerte si la trésorerie estimée descend sous :</div>
            
            <div style={{ display: 'flex', gap: '8px', marginTop: '10px', alignItems: 'center' }}>
              <div style={{ position: 'relative', flex: 1 }}>
                <input
                  type="number"
                  value={threshold}
                  onChange={(e) => setThreshold(e.target.value)}
                  style={{ width: '100%', padding: '6px 12px 6px 20px', border: '1px solid #E8E7E3', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box', fontWeight: '600' }}
                />
                <span style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', color: '#73726C', fontSize: '12px' }}>€</span>
              </div>
              <button
                type="submit"
                disabled={savingThreshold}
                style={{ background: '#1D9E75', color: 'white', border: 'none', borderRadius: '6px', padding: '6px 12px', fontSize: '12px', fontWeight: '500', cursor: 'pointer', transition: 'background 0.2s' }}
              >
                {savingThreshold ? 'Enr...' : 'Valider'}
              </button>
            </div>
          </form>
          <div style={{ fontSize: '11px', color: '#73726C', marginTop: '12px' }}>
            Seuil actuel : <strong style={{ color: '#2D2D2D' }}>{formatEuro(data.alert_threshold)}</strong>
          </div>
        </div>

      </div>

      {/* 2. FORECAST ALERTS BLOCK */}
      {hasForecastAlert ? (
        <div className="forecast-alert" style={{ background: '#FEE2E2', border: '1px solid #FCA5A5', borderRadius: '12px', padding: '16px', marginBottom: '20px', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
          <span style={{ fontSize: '24px' }}>⚠️</span>
          <div>
            <div style={{ fontWeight: '700', color: '#991B1B', fontSize: '14px' }}>Alerte : Trésorerie critique projetée</div>
            <div style={{ fontSize: '12.5px', color: '#7F1D1D', marginTop: '4px', lineHeight: '1.4' }}>
              Selon l'historique de vos flux, votre solde de trésorerie estimé risque de passer sous votre seuil critique de <strong>{formatEuro(data.alert_threshold)}</strong> à partir de <strong>{alertMonth?.nom}</strong> (solde estimé : <strong style={{ color: '#B91C1C' }}>{formatEuro(alertMonth?.solde_estime)}</strong>).
            </div>
            <div style={{ fontSize: '11.5px', color: '#7F1D1D', marginTop: '8px', fontWeight: '500' }}>
              💡 Suggestions de réaction : Reportez ou échelonnez vos dépenses non urgentes, relancez vos factures clients en attente de paiement, ou rapprochez vos transactions bancaires.
            </div>
          </div>
        </div>
      ) : (
        <div className="forecast-alert" style={{ background: '#E1F5EE', border: '1px solid #A7F3D0', borderRadius: '12px', padding: '16px', marginBottom: '20px', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
          <span style={{ fontSize: '24px' }}>✅</span>
          <div>
            <div style={{ fontWeight: '700', color: '#065F46', fontSize: '14px' }}>Trésorerie saine sous surveillance</div>
            <div style={{ fontSize: '12.5px', color: '#047857', marginTop: '4px', lineHeight: '1.4' }}>
              Aucune baisse sous le seuil critique de <strong>{formatEuro(data.alert_threshold)}</strong> n'est projetée sur les 3 prochains mois. Vos finances restent stables par rapport à votre moyenne d'activité.
            </div>
          </div>
        </div>
      )}

      {/* 3. HISTORICAL & FORECAST CHARTS */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '20px', marginBottom: '20px' }}>
        
        {/* Main Chart Card */}
        <div className="card" style={{ background: 'white', borderRadius: '12px', border: '1px solid #E8E7E3', padding: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <div>
              <div style={{ fontSize: '14px', fontWeight: '600', color: '#2D2D2D' }}>Évolution des flux et de la trésorerie</div>
              <div style={{ fontSize: '11.5px', color: '#73726C', marginTop: '2px' }}>Comparaison mensuelle des flux (barres) et du solde cumulé de fin de mois (courbe)</div>
            </div>
          </div>

          <div style={{ width: '100%', height: '350px' }}>
            <ResponsiveContainer>
              <ComposedChart data={data.monthly_flows} margin={{ top: 20, right: 5, left: -10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F3F4F6" />
                <XAxis dataKey="nom" stroke="#9CA3AF" fontSize={11} tickLine={false} />
                <YAxis yAxisId="left" stroke="#9CA3AF" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `${v / 1000}k€`} />
                <YAxis yAxisId="right" orientation="right" stroke="#9CA3AF" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `${v / 1000}k€`} />
                <Tooltip
                  formatter={(value, name) => {
                    if (name === 'Solde Cumulé') return [formatEuro(value), name];
                    return [formatEuro(value), name];
                  }}
                  contentStyle={{ background: 'white', borderRadius: '8px', border: '1px solid #E8E7E3', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', fontSize: '12px' }}
                />
                <Legend verticalAlign="top" height={36} iconType="circle" iconSize={8} wrapperStyle={{ fontSize: '12px' }} />
                <Bar yAxisId="left" dataKey="entrees" name="Entrées (+)" fill="#1D9E75" radius={[4, 4, 0, 0]} maxBarSize={25} />
                <Bar yAxisId="left" dataKey="sorties" name="Sorties (-)" fill="#EF9F27" radius={[4, 4, 0, 0]} maxBarSize={25} />
                <Line yAxisId="right" type="monotone" dataKey="solde_fin" name="Solde Cumulé" stroke="#3B82F6" strokeWidth={3} dot={{ r: 4, strokeWidth: 1 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

      </div>

      {/* 4. FORECAST TABLE & TOP EXPENSES */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '20px' }}>
        
        {/* Forecast Table Card */}
        <div className="card" style={{ background: 'white', borderRadius: '12px', border: '1px solid #E8E7E3', padding: '20px' }}>
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '14px', fontWeight: '600', color: '#2D2D2D' }}>Prévisions de Trésorerie (3 mois)</div>
            <div style={{ fontSize: '11.5px', color: '#73726C', marginTop: '2px' }}>Estimation basée sur vos flux moyens</div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {data.forecast.map((item, idx) => (
              <div
                key={idx}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px',
                  background: item.alerte ? '#FEE2E2' : '#FAF9F5',
                  border: item.alerte ? '1px solid #FCA5A5' : '1px solid #E8E7E3',
                  borderRadius: '8px'
                }}
              >
                <div>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: '#2D2D2D' }}>{item.nom}</div>
                  <div style={{ fontSize: '11px', color: '#73726C', marginTop: '2px' }}>
                    Entrées/Sorties : +{formatEuro(item.entrees)} / -{formatEuro(item.sorties)}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '14px', fontWeight: '700', color: item.alerte ? '#B91C1C' : '#0F6E56' }}>
                    {formatEuro(item.solde_estime)}
                  </div>
                  <div style={{ fontSize: '10px', color: item.alerte ? '#B91C1C' : '#73726C', marginTop: '2px', fontWeight: '500' }}>
                    {item.alerte ? '🚨 Sous le seuil' : '✓ Normal'}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Top Expenses Categories */}
        <div className="card" style={{ background: 'white', borderRadius: '12px', border: '1px solid #E8E7E3', padding: '20px' }}>
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '14px', fontWeight: '600', color: '#2D2D2D' }}>Principaux postes de dépenses</div>
            <div style={{ fontSize: '11.5px', color: '#73726C', marginTop: '2px' }}>Répartition des débits bancaires par catégorie</div>
          </div>

          {data.top_categories && data.top_categories.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '16px' }}>
              {data.top_categories.map((cat, idx) => (
                <div key={idx} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
                    <span style={{ color: '#2D2D2D', fontWeight: '500' }}>
                      {cat.categorie.replace('_', ' ').toUpperCase()}
                    </span>
                    <span style={{ fontWeight: '600', color: '#2D2D2D' }}>
                      {formatEuro(cat.total)}
                    </span>
                  </div>
                  {/* Progress bar */}
                  <div style={{ width: '100%', height: '6px', background: '#FAF9F5', borderRadius: '3px', overflow: 'hidden' }}>
                    <div
                      style={{
                        height: '100%',
                        borderRadius: '3px',
                        background: COLORS[idx % COLORS.length],
                        width: `${Math.min(100, (cat.total / Math.max(...data.top_categories.map(c => c.total))) * 100)}%`
                      }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ textAlign: 'center', color: '#73726C', fontSize: '12.5px', padding: '40px 0' }}>
              Aucun débit catégorisé sur cet exercice. Importez un relevé bancaire pour analyser vos postes de dépenses.
            </div>
          )}
        </div>

      </div>

    </div>
  );
};

export default CashFlow;
